import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { FollowupActionsForm } from '../visits/FollowupActionsForm';
import { api } from '../../services/api';
import type { TourLine } from '../../types/deliveryTour';
import { lineLabel } from '../../types/deliveryTour';
import type { FollowupPriority } from '../../utils/followupPriority';

interface PendingFollowup {
  id: string;
  followup_notes?: string;
}

export interface CompactVisitPayload {
  notes?: string;
  resolves_visit_id?: string;
  needs_followup?: boolean;
  followup_materials?: string[];
  followup_actions?: string[];
  followup_notes?: string;
  followup_priority?: FollowupPriority;
  product_placements?: Record<string, { secured: boolean }>;
  visit_products?: string[];
}

interface CompactVisitFormProps {
  posId: string;
  items: TourLine[];
  saving: boolean;
  onCancel: () => void;
  onSubmit: (payload: CompactVisitPayload) => void;
}

export const CompactVisitForm: React.FC<CompactVisitFormProps> = ({
  posId,
  items,
  saving,
  onCancel,
  onSubmit,
}) => {
  const products = uniqueProducts(items);
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState<string[]>(products.map((p) => p.id));
  const [placed, setPlaced] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingFollowup | null>(null);
  const [resolveFollowup, setResolveFollowup] = useState(false);
  const [needsFollowup, setNeedsFollowup] = useState(false);
  const [followupMaterials, setFollowupMaterials] = useState<string[]>([]);
  const [followupActions, setFollowupActions] = useState<string[]>([]);
  const [followupNotes, setFollowupNotes] = useState('');
  const [followupPriority, setFollowupPriority] = useState<FollowupPriority>('medium');

  useEffect(() => {
    void api
      .get(`/followups/pos/${posId}`)
      .then((res) => {
        const row = res.data as PendingFollowup | null;
        setPending(row?.id ? row : null);
      })
      .catch(() => setPending(null));
  }, [posId]);

  const submit = () => {
    const product_placements: Record<string, { secured: boolean }> = {};
    for (const id of placed) {
      product_placements[id] = { secured: true };
    }
    onSubmit({
      notes: notes || undefined,
      resolves_visit_id: resolveFollowup && pending ? pending.id : undefined,
      needs_followup: needsFollowup,
      followup_materials: needsFollowup ? followupMaterials : undefined,
      followup_actions: needsFollowup ? followupActions : undefined,
      followup_notes: needsFollowup ? followupNotes : undefined,
      followup_priority: needsFollowup ? followupPriority : undefined,
      product_placements:
        Object.keys(product_placements).length > 0
          ? product_placements
          : undefined,
      visit_products: selected.length > 0 ? selected : undefined,
    });
  };

  return (
    <div className="space-y-4">
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Visit notes"
        className="w-full min-h-[88px] rounded-xl border px-3 py-2 text-sm"
      />
      {products.length > 0 ? (
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2">
            Products
          </p>
          {products.map((p) => (
            <label key={p.id} className="flex items-center gap-2 py-1 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(p.id)}
                onChange={() =>
                  setSelected((cur) =>
                    cur.includes(p.id)
                      ? cur.filter((id) => id !== p.id)
                      : [...cur, p.id]
                  )
                }
              />
              <span className="flex-1">{p.label}</span>
              <label className="flex items-center gap-1 text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={placed.includes(p.id)}
                  onChange={() =>
                    setPlaced((cur) =>
                      cur.includes(p.id)
                        ? cur.filter((id) => id !== p.id)
                        : [...cur, p.id]
                    )
                  }
                />
                placed
              </label>
            </label>
          ))}
        </div>
      ) : null}
      {pending ? (
        <label className="flex items-center gap-2 text-sm bg-amber-50 rounded-xl p-3">
          <input
            type="checkbox"
            checked={resolveFollowup}
            onChange={(e) => setResolveFollowup(e.target.checked)}
          />
          Resolve open follow-up
        </label>
      ) : null}
      <FollowupActionsForm
        needsFollowup={needsFollowup}
        onNeedsFollowupChange={setNeedsFollowup}
        materials={followupMaterials}
        onMaterialsChange={setFollowupMaterials}
        actions={followupActions}
        onActionsChange={setFollowupActions}
        notes={followupNotes}
        onNotesChange={setFollowupNotes}
        priority={followupPriority}
        onPriorityChange={setFollowupPriority}
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 min-h-[48px] rounded-xl bg-gray-100 font-bold"
        >
          Back
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={submit}
          className="flex-[2] min-h-[48px] rounded-xl bg-brand-600 text-white font-black disabled:opacity-50"
        >
          Deliver + visit
        </button>
      </div>
    </div>
  );
};

function uniqueProducts(items: TourLine[]): { id: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const item of items) {
    if (!item.product_id) continue;
    if (!seen.has(item.product_id)) {
      seen.set(item.product_id, lineLabel(item));
    }
  }
  return [...seen.entries()].map(([id, label]) => ({ id, label }));
}

export const SkipForm: React.FC<{
  saving: boolean;
  onCancel: () => void;
  onSubmit: (reason: 'closed' | 'refused' | 'no_access' | 'other', note?: string) => void;
}> = ({ saving, onCancel, onSubmit }) => {
  const [reason, setReason] = useState<
    'closed' | 'refused' | 'no_access' | 'other'
  >('closed');
  const [note, setNote] = useState('');
  const reasons = [
    { id: 'closed' as const, label: 'Closed' },
    { id: 'refused' as const, label: 'Refused' },
    { id: 'no_access' as const, label: 'No access' },
    { id: 'other' as const, label: 'Other' },
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {reasons.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setReason(r.id)}
            className={`px-3 py-2 rounded-xl text-sm font-bold ${
              reason === r.id ? 'bg-gray-900 text-white' : 'bg-gray-100'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note"
        className="w-full min-h-[44px] rounded-xl border px-3 text-sm"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 min-h-[48px] rounded-xl bg-gray-100 font-bold"
        >
          Back
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => onSubmit(reason, note || undefined)}
          className="flex-[2] min-h-[48px] rounded-xl bg-amber-600 text-white font-black disabled:opacity-50"
        >
          Skip stop
        </button>
      </div>
    </div>
  );
};

export const SheetHeader: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
}> = ({ title, subtitle, onClose }) => (
  <div className="flex items-start justify-between gap-3 pb-3 border-b">
    <div className="min-w-0">
      <h2 className="text-lg font-black text-gray-900 truncate">{title}</h2>
      {subtitle ? (
        <p className="text-sm text-gray-500">{subtitle}</p>
      ) : null}
    </div>
    <button type="button" onClick={onClose} className="p-2 text-gray-400" aria-label="Close">
      <X size={22} />
    </button>
  </div>
);
